import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, ExternalLink, TrendingUp, BarChart3, Server, Shield } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Card } from '../ui/card.js';

interface FailureEvent {
  id: string;
  key: string;
  text: string;
  tags: string[];
  metadata: {
    source: string;
    createdAt: string;
    eventId?: string;
    timestamp?: number;
    details?: Record<string, unknown>;
    scope?: string;
  };
}

interface FailuresResponse {
  success: boolean;
  data: FailureEvent[];
  count: number;
  limit: number;
}

export const ResiliencePanel: React.FC = () => {
  const [failures, setFailures] = useState<FailureEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'failures' | 'metrics' | 'infrastructure' | 'resilience'>('failures');

  useEffect(() => {
    fetchFailures();
  }, []);

  const fetchFailures = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/failures?limit=5');
      const data: FailuresResponse = await response.json();
      
      if (data.success) {
        setFailures(data.data);
      } else {
        setError('Failed to fetch failure events');
      }
    } catch (err) {
      setError('Network error while fetching failures');
      console.error('Failed to fetch failures:', err);
    } finally {
      setLoading(false);
    }
  };

  const testFailureInjection = async () => {
    try {
      const response = await fetch('/api/test-resilience-failure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        console.log('Failure injection triggered successfully');
      }
      
      setTimeout(fetchFailures, 1000);
    } catch (err) {
      console.error('Failed to trigger test failure:', err);
    }
  };

  const runSupervisorCheck = async () => {
    try {
      const response = await fetch('/api/bees/SupervisorBee/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentId: 'system', checkAll: true })
      });
      
      const result = await response.json();
      console.log('SupervisorBee check result:', result);
      
      if (result.success) {
        setError(null);
        setTimeout(fetchFailures, 500);
      } else {
        setError(`Supervisor check failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to run supervisor check:', err);
      setError('Failed to run supervisor check');
    }
  };

  const runFailureDetection = async () => {
    try {
      const response = await fetch('/api/bees/FailureDetectionBee/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: 'current-session' })
      });
      
      const result = await response.json();
      console.log('FailureDetectionBee result:', result);
      
      if (result.success) {
        setError(null);
        setTimeout(fetchFailures, 500);
      } else {
        setError(`Failure detection failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to run failure detection:', err);
      setError('Failed to run failure detection');
    }
  };

  const formatTimestamp = (timestamp: string | number) => {
    const date = new Date(typeof timestamp === 'string' ? timestamp : timestamp);
    return date.toLocaleString();
  };

  const getSourceColor = (source: string) => {
    switch (source.toLowerCase()) {
      case 'supervisorbee':
        return 'text-blue-400 bg-blue-500/20';
      case 'servermonitoring':
        return 'text-purple-400 bg-purple-500/20';
      case 'httpserver':
        return 'text-red-400 bg-red-500/20';
      case 'serverstartup':
        return 'text-yellow-400 bg-yellow-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const extractPRNumber = (text: string): number | null => {
    const prMatch = text.match(/PR #?(\d+)/i);
    return prMatch ? parseInt(prMatch[1]) : null;
  };

  const renderGrafanaDashboard = (dashboardId: string, title: string) => {
    const grafanaBaseUrl = import.meta.env.VITE_GRAFANA_URL || 'http://localhost:3001';
    const iframeSrc = `${grafanaBaseUrl}/d/${dashboardId}?orgId=1&refresh=30s&theme=dark&kiosk=tv`;
    
    return (
      <div className="h-[600px] w-full">
        <iframe
          src={iframeSrc}
          title={title}
          className="w-full h-full border border-card-border rounded-lg bg-card"
          frameBorder="0"
          allowFullScreen
        />
      </div>
    );
  };

  const tabs = [
    { id: 'failures' as const, label: 'Failures', icon: AlertTriangle },
    { id: 'metrics' as const, label: 'Metrics', icon: BarChart3 },
    { id: 'infrastructure' as const, label: 'Infrastructure', icon: Server },
    { id: 'resilience' as const, label: 'Resilience', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
        <span className="ml-3 text-text-muted">Loading failure events...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertTriangle className="w-12 h-12 text-red-400" />
        <p className="text-red-400">{error}</p>
        <Button onClick={fetchFailures} variant="secondary" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text">System Resilience & Observability</h3>
          <p className="text-text-muted">Monitoring, metrics, and failure analysis</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            onClick={runSupervisorCheck} 
            variant="default" 
            size="sm"
            data-devinid="supervisor-check-button"
          >
            Run Supervisor Check
          </Button>
          <Button 
            onClick={runFailureDetection} 
            variant="secondary" 
            size="sm"
            data-devinid="failure-detection-button"
          >
            Detect Failures
          </Button>
          <Button 
            onClick={testFailureInjection} 
            variant="secondary" 
            size="sm"
            data-devinid="resilience-test-button"
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Test Failure
          </Button>
          <Button onClick={fetchFailures} variant="secondary" size="sm">
            Refresh
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-surface-hover p-1 rounded-lg">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-tyler-blue text-white'
                  : 'text-text-muted hover:text-text hover:bg-surface-hover'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'failures' && (
          <div>
            {failures.length === 0 ? (
              <Card className="glass-dark p-8 text-center">
                <TrendingUp className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-text mb-2">All Systems Healthy</h4>
                <p className="text-text-muted">No recent failure events detected</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {failures.map((failure) => {
                  const prNumber = extractPRNumber(failure.text);
                  
                  return (
                    <Card key={failure.id} className="glass-dark p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                          <div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(failure.metadata.source)}`}>
                              {failure.metadata.source}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center text-text-muted text-sm">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatTimestamp(failure.metadata.createdAt)}
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-text font-medium mb-1">Error Event</p>
                        <p className="text-text-muted text-sm leading-relaxed">
                          {failure.text}
                        </p>
                      </div>

                      {failure.metadata.details && (
                        <details className="mb-3">
                          <summary className="text-text-muted text-sm cursor-pointer hover:text-text">
                            View Details
                          </summary>
                          <div className="mt-2 p-3 glass-dark">
                            <pre className="text-xs text-text-muted whitespace-pre-wrap overflow-x-auto">
                              {typeof failure.metadata.details === 'string' 
                                ? failure.metadata.details 
                                : JSON.stringify(failure.metadata.details, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}

                      {prNumber && (
                        <div className="flex items-center justify-end">
                          <a
                            href={`https://github.com/DDPMLS/beehive-core/pull/${prNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-tyler-blue hover:text-accent text-sm"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>View PR #{prNumber}</span>
                          </a>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'metrics' && (
          <div>
            <div className="mb-4">
              <h4 className="text-lg font-medium text-text mb-2">Bee Performance Metrics</h4>
              <p className="text-text-muted">Execution rates, latency percentiles, and performance summary</p>
            </div>
            {renderGrafanaDashboard('beehive-metrics', 'beehive-core - Bee Metrics')}
          </div>
        )}

        {activeTab === 'infrastructure' && (
          <div>
            <div className="mb-4">
              <h4 className="text-lg font-medium text-text mb-2">Infrastructure Monitoring</h4>
              <p className="text-text-muted">CPU/memory usage, queue depths, and Node.js runtime metrics</p>
            </div>
            {renderGrafanaDashboard('beehive-infrastructure', 'beehive-core - Infrastructure Metrics')}
          </div>
        )}

        {activeTab === 'resilience' && (
          <div>
            <div className="mb-4">
              <h4 className="text-lg font-medium text-text mb-2">Resilience Dashboard</h4>
              <p className="text-text-muted">Failure rates, recovery times, service health status, and SLO tracking</p>
            </div>
            {renderGrafanaDashboard('beehive-resilience', 'beehive-core - Resilience Metrics')}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ResiliencePanel;
